/**
 * Turning a run's events into something a person can read.
 *
 * A line reads the way the design writes it: where the run is, then the step's
 * own sentence, then what that step actually did. "2.1/4 · Chase the supplier"
 * over "Note left for the supplier".
 *
 * An event is a `kind` plus a loose snapshot, so everything here is written to
 * survive a kind, party or outcome nobody has seen yet: an unknown value is
 * humanised rather than dropped, and never throws. The service types `kind` as
 * a bare string for exactly that reason — the names below are Radar's, not the
 * service's, and it stores whatever it is sent.
 *
 * How a run is *labelled* lives in AOPExecutionStates — that is a fold of two
 * fields and both tabs share it.
 */

import moment from 'moment-timezone';
import { AOPEvent, AOPEventSnapshot, AOPExecution } from '../../../types/AOPExecutionTypes';
import { AOPPlan } from '../../../types/AOPTypes';
import {
	exitLabel,
	humaniseExitName,
	partyLabel,
	sentenceCase,
	stepNumberOf,
} from './AOPPlanUtils';

/**
 * How a run ended, in the words the funnel and the row badge already use.
 *
 * They read "Successful" and "Unsuccessful" over in `AOP_RUN_STATES`, so a feed
 * line calling the same run's end "Goal successful" was a second vocabulary for
 * one fact — and "goal" is a word this feature uses nowhere else.
 */
const OUTCOME_TITLES: { [outcome: string]: string } = {
	success: 'Successful.',
	failure: 'Unsuccessful.',
	cancelled: 'Cancelled.',
};

/**
 * Kinds a feed leaves out.
 *
 * `communication_attempted` is written immediately before Radar writes the note
 * and `communication_succeeded` immediately after, so it is the same line
 * twice. A note that fails is not reported at all today; a kind that says so is
 * where the failure belongs, rather than an attempt left on screen for the
 * reader to interpret.
 */
const HIDDEN_KINDS = ['communication_attempted'];

/** Whether an event earns a line of its own. */
export const isListedEvent = (event: AOPEvent): boolean => HIDDEN_KINDS.indexOf(event.kind) < 0;

/** The step the run is on, or was on when it ended. */
export const currentStepLabelOf = (execution: AOPExecution): string | null => {
	const step = execution.latest ? execution.latest.snapshot.current_step : undefined;
	return step && step.description ? sentenceCase(step.description) : null;
};

/**
 * Where the event sits in the plan the run pinned: "2/4", "2.1/4" when a chase
 * has gone out, or "Finish" on the step that ends it.
 *
 * The plan has to be passed in because the event does not carry a position —
 * `step_count` counts the steps a run has *entered*, which is not where it is.
 * A run pins a published version, so the plan is that version's, not the
 * draft's; the tab reads it from the versions it already holds. Null when the
 * version has not loaded, or when the step is not in it — both are "we do not
 * know", and a wrong number here would be worse than none.
 */
export const positionOf = (event: AOPEvent, plan: AOPPlan | null): string | null => {
	const snapshot = event.snapshot;
	const stepName = snapshot && snapshot.current_step ? snapshot.current_step.name : null;

	if (!plan || !plan.steps || !stepName) {
		return null;
	}

	const index = plan.steps.findIndex((step) => step.name === stepName);

	if (index < 0) {
		return null;
	}

	if (plan.steps[index].type === 'finish') {
		return 'Finish';
	}

	// `escalation` counts chases already sent, and only a `waiting` event carries
	// it: 0 is the first silent wait, so only 1 and up name a chase.
	const chase = snapshot.escalation;
	const step = chase ? `${stepNumberOf(index)}.${chase}` : `${stepNumberOf(index)}`;

	return `${step}/${plan.steps.length}`;
};

/**
 * An event as two lines: what Radar did, and who it was pointed at.
 *
 * `title` is a sentence about the event itself rather than the step's stored
 * label. The compiler writes labels as lowercase verb phrases — "watch for
 * somebody on site", "assign next supplier" — which read as instructions to
 * Radar, not as a record of what happened; only `step_entered`, whose whole
 * subject is the step, shows one.
 *
 * `supplier` leads the second line, because who the work order is with is the
 * thing a reader wants on every row and it changes mid-run when Radar
 * reassigns. `detail` is whatever that event adds beyond it.
 */
export interface AOPEventDescription {
	/**
	 * The sentence, stopped — except where `actor` is set, when it is the stem
	 * the person goes on: "Paused by". The full stop then belongs to whoever
	 * renders the name, because the name is a component and not a word.
	 */
	title: string;
	/**
	 * The address of whoever asked for this, where the event names one — a
	 * pause, a resume, or a cancel somebody requested.
	 *
	 * Kept out of the sentence so a screen can render the person rather than
	 * their email. The service gives an address and nothing else, so a reader
	 * saw `arnav+sadmin@useopenwrench.com` where every other surface in the app
	 * shows a name.
	 */
	actor: string | null;
	/** Who the run was pointed at. Rendered as a link, so it stays structured. */
	supplier: { id: string; name: string } | null;
	detail: string | null;
}

/** What `titleOf` answers: the words, and the person they name if any. */
type AOPEventSentence = Pick<AOPEventDescription, 'title' | 'actor'>;

/**
 * A feed line is a sentence, so it is stopped as well as capitalised — unlike a
 * step's name on a block, which is a name and takes no full stop.
 */
/** snake_case to a phrase. The exit helper does it; the name is its history. */
const humaniseKind = humaniseExitName;

const asSentence = (text: string): string => {
	const opened = sentenceCase(text);
	return /[.!?]$/.test(opened) ? opened : `${opened}.`;
};

const stepLabelOf = (snapshot: AOPEventSnapshot): string | null =>
	snapshot.current_step && snapshot.current_step.description
		? snapshot.current_step.description
		: null;

/** The party as the picker names it. */
const toParty = (snapshot: AOPEventSnapshot): string => partyLabel(snapshot.party);

/**
 * When Radar will look again, in the work order's own zone.
 *
 * The timeline renders every event's timestamp in the site's zone, so this
 * sentence sitting beside it in the reader's zone put two unlabelled clocks on
 * one row. The zone is named either way: the procedure-wide feed spans work
 * orders and has no single site to use, so there it says which zone it is in
 * rather than implying the site's.
 */
const untilOf = (snapshot: AOPEventSnapshot, timeZoneId?: string): string | null => {
	if (!snapshot.waiting_until) {
		return null;
	}

	const at = timeZoneId
		? moment.tz(snapshot.waiting_until, timeZoneId)
		: moment(snapshot.waiting_until);

	return at.format('MMM D, h:mm A z');
};

/** A sentence that names nobody. */
const plain = (title: string): AOPEventSentence => ({ title, actor: null });

/** The stem for a sentence that does, with the person it belongs to. */
const by = (stem: string, actor: string): AOPEventSentence => ({ title: stem, actor });

/** What Radar did, as a sentence, and the person it names where it names one. */
const titleOf = (event: AOPEvent, snapshot: AOPEventSnapshot): AOPEventSentence => {
	switch (event.kind) {
		case 'started':
			return plain('Radar started on this work order.');
		case 'step_entered': {
			const label = stepLabelOf(snapshot);
			return plain(label ? asSentence(label) : 'Moved on to the next step.');
		}
		case 'communication_succeeded':
			// Who it was for is the only thing that varies — the channel is always a
			// note on the work order — so it belongs in the sentence rather than
			// trailing after it as a fragment.
			return plain(
				snapshot.party
					? `Added a note for the ${toParty(snapshot)}.`
					: 'Added a note to the work order.'
			);
		case 'waiting': {
			// `escalation` counts chases already sent: 0 is the first silent wait.
			const chases = snapshot.escalation || 0;
			return plain(
				chases === 0
					? 'Waiting for a reply.'
					: `Still waiting, after ${chases} reminder${chases === 1 ? '' : 's'}.`
			);
		}
		case 'decided':
			/*
			  The exit taken is the decision, so it is the sentence.
			
			  It used to read "Moved on." for every exit, with the exit underneath —
			  so a supplier accepting a job was titled as the step giving up on them.
			  Only the two reserved exits are a give-up, and `EXIT_LABELS` already
			  words both as one: "No reply in time", "No reply after every reminder".
			  Every other exit is an answer, and reads as itself.
			*/
			return plain(snapshot.reason ? asSentence(exitLabel(snapshot.reason)) : 'Moved on.');
		case 'assigned':
			// `openwrench` means a person moved it while Radar was watching.
			return plain(
				snapshot.assigned_by === 'radar'
					? 'Radar assigned the next supplier.'
					: 'The work order was reassigned in OpenWrench.'
			);
		/*
		  Named in the sentence, the way a cancel is.
		
		  These read as "Paused." with "By Arnav." underneath, which is the same
		  fact in two fragments — and the work order's own Radar header says
		  "Paused by Arnav at 2:31 PM" in one. Three places describe these three
		  kinds; this was the one that disagreed.
		*/
		case 'paused':
			return snapshot.requested_by ? by('Paused by', snapshot.requested_by) : plain('Paused.');
		case 'resumed':
			return snapshot.requested_by ? by('Resumed by', snapshot.requested_by) : plain('Resumed.');
		case 'finished': {
			const outcome = snapshot.outcome;

			/*
			  A cancel somebody asked for, told apart from a run the work order
			  stopped. `requested_by` is only on a finish a buyer cancelled, so its
			  presence is the distinction — both used to read "Cancelled." and leave
			  the reader to guess which had happened.

			  What the other case cannot yet say is what stopped it; the work order's
			  status is not on the event.
			*/
			if (outcome === 'cancelled' && snapshot.requested_by) {
				return by('Cancelled by', snapshot.requested_by);
			}

			return plain((outcome && OUTCOME_TITLES[outcome]) || 'Finished.');
		}
		default:
			// A kind this build has never seen. Showing it beats hiding it, and
			// `humaniseExitName` only turns snake_case into a phrase — it is named
			// for where it was first needed, not for what it does.
			return plain(asSentence(humaniseKind(event.kind)));
	}
};

/** What this event adds beyond the supplier on the line. */
const detailOf = (
	event: AOPEvent,
	snapshot: AOPEventSnapshot,
	timeZoneId?: string
): string | null => {
	const reason = snapshot.reason;

	switch (event.kind) {
		// No `decided`: the exit is the title now, and repeating it underneath
		// said the same thing twice.
		case 'waiting': {
			const until = untilOf(snapshot, timeZoneId);
			return until ? `Next check ${until}.` : null;
		}
		/*
		  Whatever was said about it, where something was.
		
		  Who did it used to be here too, for a pause and a resume; it is in the
		  title now, beside the word for what they did — which left these three
		  saying the same thing and they are one case.
		*/
		case 'paused':
		case 'resumed':
		case 'finished':
			return reason ? asSentence(reason) : null;
		default:
			return null;
	}
};

/**
 * `timeZoneId` is the work order's, where the caller knows it. The procedure's
 * feed spans work orders, so it has none to give and the times say which zone
 * they are in instead.
 */
export const describeEvent = (event: AOPEvent, timeZoneId?: string): AOPEventDescription => {
	const snapshot = event.snapshot || ({} as AOPEventSnapshot);
	const detail = detailOf(event, snapshot, timeZoneId);

	return {
		...titleOf(event, snapshot),
		supplier: snapshot.supplier && snapshot.supplier.name ? snapshot.supplier : null,
		detail: detail ? detail : null,
	};
};
